import { message, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import ScrollPanel from '../../components/ScrollPanel';

import SplitPane from 'react-split-pane';

import { usePostHog } from '@posthog/react';
import { getUroSDK, getUroSocket } from '../../services/URO';
import colors from '../../utils/theme/colors';
import WorkflowConfigCol from './components/WorkflowConfigCol';
import WorkflowJobsList from './components/WorkflowJobsList';
import { useLocation } from 'react-router';

function WorkflowPlaygroundScreen() {
  // All hooks must be called before any conditional return!
  const location = useLocation();
  const posthog = usePostHog();
  // Switch to 2-panel layout (config + jobs), sizes no longer persisted per panel
  const [workflows, setWorkflows] = useState({
    loading: true,
    _error: null,
    data: [],
    params: {
      workflow_name: '',
    },
  });
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [workflowJobs, setWorkflowJobs] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1,
  });
  const fetchWorkflows = useCallback(async (params) => {
    try {
      setWorkflows((prev) => ({ ...prev, loading: true, _error: null }));
      const { data } = await getUroSDK().listWorkflows({
        limit: params?.limit || 100,
        page: params?.page || 1,
        name: params?.name || null,
      });
      setWorkflows((prev) => ({
        ...prev,
        loading: false,
        data: data.items,
        params: prev.params,
      }));
    } catch (error) {
      setWorkflows((prev) => ({ ...prev, loading: false, _error: error }));
      message.error('Failed to fetch workflows');
    }
  }, []);

  const fetchJobs = async (page = 1, limit = 10, append = false) => {
    try {
      const { data } = await getUroSDK().listWorkflowJobs({ page, limit });
      if (append) {
        setWorkflowJobs((prev) => {
          const incoming = data.items || [];
          if (incoming.length === 0) return prev;
          const next = [...prev];
          incoming.forEach((item) => {
            const i = next.findIndex((j) => j.workflow_job_id === item.workflow_job_id);
            if (i >= 0) {
              next[i] = {
                ...next[i],
                ...item,
                results: item.results !== undefined ? item.results : next[i].results,
                error: item.error !== undefined ? item.error : next[i].error,
              };
            } else {
              next.push(item);
            }
          });
          return next;
        });
        // Update pagination to reflect the new current page for append operations
        setPagination(prev => ({
          page: data?.pagination?.page ?? page,
          limit: data?.pagination?.limit ?? limit,
          totalItems: data?.pagination?.totalItems ?? prev.totalItems,
          totalPages: data?.pagination?.totalPages ?? prev.totalPages,
        }));
      } else {
        setWorkflowJobs(data.items || []);
        setPagination({
          page: data?.pagination?.page ?? page,
          limit: data?.pagination?.limit ?? limit,
          totalItems: data?.pagination?.totalItems ?? (data.items?.length || 0),
          totalPages: data?.pagination?.totalPages ?? 1,
        });
      }
    } catch (error) {
      console.error('Failed to fetch jobs:', error?.message);
      message.error('Failed to fetch jobs');
    }
  };

  const handlePageChange = (page, pageSize) => {
    fetchJobs(page, pageSize);
  };

  const handleLoadMore = (page) => {
    fetchJobs(page, pagination.limit, true);
  };

  const addOrUpdateJob = useCallback((job) => {
    if (!job.workflow_job_id) {
      // eslint-disable-next-line no-console
      console.error('Job missing workflow_job_id:', job);
      return;
    }
    setWorkflowJobs((prev) => {
      const idx = prev.findIndex((j) => j.workflow_job_id === job.workflow_job_id);
      if (idx >= 0) {
        const existing = prev[idx];
        const merged = {
          ...existing,
          ...job,
          results: job.results !== undefined ? job.results : existing.results,
          error: job.error !== undefined ? job.error : existing.error,
        };
        const next = [...prev];
        next[idx] = merged;
        return next;
      }
      return [job, ...prev];
    });
  }, []);

  const handleWorkflowSelect = (workflow) => {
    // PostHog: Capture workflow selection
    posthog?.capture('workflow_selected', {
      workflow_definition_id: workflow.workflow_definition_id,
      workflow_name: workflow.name,
    });

    setSelectedWorkflow(workflow);
  };

  const handleRunWorkflow = async (params) => {
    try {
      await getUroSDK().runWorkflow({
        workflow_definition_id: params.workflow_definition_id,
        input_params: params.params,
      });

      // PostHog: Capture workflow run submission
      posthog?.capture('workflow_run_submitted', {
        workflow_definition_id: params.workflow_definition_id,
        workflow_name: selectedWorkflow?.name,
      });

      message.success('Workflow job submitted');
    } catch (error) {
      console.error('Failed to run workflow:', error?.response?.status || error?.message);
      posthog?.capture('workflow_run_failed', {
        workflow_definition_id: params.workflow_definition_id,
        error: error?.message,
      });
      message.error('Failed to run workflow');
    }
  };

  const fetchWorkflow = useCallback(async (id) => {
    return await getUroSDK()
      .getWorkflowById(id)
      .then(({ data }) => data);
  }, []);

  useEffect(() => {
    const socket = getUroSocket();
    fetchWorkflows(workflows.params); // Only run on mount
    fetchJobs(1, pagination.limit); // Initial fetch with default limit

    const onCreated = (data) => {
      addOrUpdateJob({ ...data, status: 'pending' });
    };
    const onProcessing = (data) => {
      addOrUpdateJob({ ...data, status: 'processing' });
    };
    const onComplete = (data) => {
      addOrUpdateJob({ ...data, status: 'completed', results: data.results });
      // Force-refresh to ensure UI reflects server truth
      fetchJobs(1, 10);
    };
    const onFailed = (data) => {
      addOrUpdateJob({ ...data, status: 'failed', error: data.error });
      // Force-refresh to ensure UI reflects server truth
      fetchJobs(1, 10);
    };
    const onBalanceChanged = () => {};

    socket.on('workflow:created', onCreated);
    socket.on('workflow:processing', onProcessing);
    socket.on('workflow:complete', onComplete);
    socket.on('workflow:failed', onFailed);
    socket.on('client:balance_changed', onBalanceChanged);

    return () => {
      if (socket && socket.off) {
        socket.off('workflow:created', onCreated);
        socket.off('workflow:processing', onProcessing);
        socket.off('workflow:complete', onComplete);
        socket.off('workflow:failed', onFailed);
        socket.off('client:balance_changed', onBalanceChanged);
      }
      // Do not disconnect here; AppLayout manages the shared socket lifecycle.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchWorkflows, addOrUpdateJob]);

  // If navigated with ?workflow_id=, preselect that workflow automatically
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const preselectId = params.get('workflow_id');
      if (preselectId) {
        fetchWorkflow(preselectId).then((wf) => setSelectedWorkflow(wf));
      }
    } catch (e) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);



  if (workflows._error) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Typography.Text type='danger'>
          {workflows._error.message}
        </Typography.Text>
      </div>
    );
  }

  return (
    <ScrollPanel
      style={{
        height: '100%',
        background: 'transparent',
        overflow: 'hidden',
      }}
    >
      <SplitPane
        split='vertical'
        defaultSize={'70%'}
        minSize={0}
        maxSize={-200}
        paneStyle={{
          display: 'flex',
          alignItems: 'stretch',
          minWidth: 0,
          height: '100%',
        }}
        style={{
          height: '100%',
          width: '100%',
          minWidth: 0,
          background: 'transparent',
          boxSizing: 'border-box',
        }}
        resizerStyle={{
          background: colors.black,
          width: 6,
          marginInline: 8,
          cursor: 'col-resize',
          zIndex: 2,
        }}
      >
        {/* Left Panel: WorkflowConfigCol (now includes embedded selector) */}
        <div
          style={{
            background: '#0a0a0a',
            padding: 20,
            borderRadius: 12,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            overflow: 'hidden',
            flex: 1,
            minHeight: 0,
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          }}
        >
          <WorkflowConfigCol
            handleRunWorkflow={handleRunWorkflow}
            selectedWorkflow={selectedWorkflow}
            fetchWorkflow={fetchWorkflow}
            workflowsState={workflows}
            fetchWorkflows={fetchWorkflows}
            onWorkflowSelect={handleWorkflowSelect}
          />
        </div>
        {/* Right Panel: WorkflowJobsList */}
        <div
          style={{
            background: '#0a0a0a',
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 8,
            minWidth: 200,
            width: '100%',
            boxSizing: 'border-box',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.05)'
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              flexWrap: 'wrap',
              marginBottom: 8,
            }}
          />
          <WorkflowJobsList
            workflowJobs={workflowJobs}
            pagination={pagination}
            onPageChange={handlePageChange}
            onLoadMore={handleLoadMore}
          />
        </div>
      </SplitPane>
    </ScrollPanel>
  );
}

export default WorkflowPlaygroundScreen;
